import { Trans, t } from '@lingui/macro'
import { IconButton, Paper, Stack, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

import type { TrustedActor } from '@/core/models/trusted-actor.model'

import { ActorAvatar } from './ActorAvatar'

type Props = {
  actors: TrustedActor[]
  isLoading: boolean
  onEdit: (actor: TrustedActor) => void
  onDelete: (actor: TrustedActor) => void
}

export function MobileList({ actors, isLoading, onEdit, onDelete }: Props) {
  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Trans>Loading...</Trans>
      </Paper>
    )
  }
  if (actors.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Trans>No trusted actors configured</Trans>
      </Paper>
    )
  }
  return (
    <Stack spacing={1.5}>
      {actors.map((actor) => (
        <Paper
          key={actor._id}
          elevation={0}
          sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <ActorAvatar actor={actor} />
            <Stack flex={1} spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {actor.name}
              </Typography>
              {actor.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {actor.description}
                </Typography>
              )}
              {actor.addedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {new Date(actor.addedAt).toLocaleString()}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={() => onEdit(actor)} aria-label={t`Edit trusted actor`}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => onDelete(actor)} aria-label={t`Remove trusted actor`}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  )
}
